import {
  Button,
  ButtonType,
  Form,
  FormFieldSize,
  FormGroup,
  IncontextBanner,
  Input,
  SubmitButton
} from '@digicert/dcone-common-ui'
import {faCopy, faPencil} from '@fortawesome/pro-light-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Translate } from 'react-localize-redux'
import { translate } from 'shared/helpers/utils'
import styles from './OpenIdConnectIntegration.module.scss'
import history from 'core/history/history'
import {
  ConfigResponse,
  OpenIdConnectIntegrationOwnProps,
  OpenIdConnectIntegrationProps,
  OpenIdConnectIntegrationStateProps
} from './OpenIdConnectIntegration.types'
import { connect } from 'react-redux'
import { IAppState } from 'core/store/store'
import { createAccountSignIn, getAccountSignIn } from '../../create-account-sign-in/AccountSignIn.actions'
import { fetchTenantById } from 'secure-pages/tenants/Tenants.actions'
import { RouterConfig } from 'core/utils/constants'
import { updateIdpMetadata } from './OpenIdConnectIntegration.actions'
import { disableSigninMethod } from 'secure-pages/tenants/create-account-sign-in/AccountSignIn.actions'
import cn from 'classnames'

const OpenIdConnectIntegration = (props: OpenIdConnectIntegrationProps): ReactElement => {
  const {createAccountSignIn, fetchTenantById, getAccountSignIn, updateIdpMetadata, tenant} = props
  const [providerUrl, setProviderUrl] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [previousClientSecret, setPreviousClientSecret] = useState('')
  const [editingClientSecret, setEditingClientSecret] = useState(false)
  const [authConfigStatus, setAuthConfigStatus] = useState(undefined)
  const [clientId, setClientId] = useState('')
  const [tokenAudience, setTokenAudience] = useState('')
  const [account, setAccount] = useState(tenant)
  const [callBackUrl, setCallBackUrl] = useState('')
  const [loginEndpoint, setLoginEndPoint] = useState('')
  const [logoutEndpoint, setLogoutEndpoint] = useState('')
  const [status, setStatus] = useState('DISABLED')
  const [federationId, setFederationId] = useState('')
  const [showInfo, setShowInfo] = useState(false)

  const payload = {
    auth_type: 'OIDC',
    account_id: props.match.params.id
  }

  const setOidcInfo = (data) => {
    if(data.auth_config && data.auth_config.find(item => item.type === 'OIDC')) {
      setAuthConfigStatus(data.auth_config?.find(item => item.type === 'OIDC').status)
    }

    if (typeof data.auth_config === 'undefined' || data.auth_config == null || data.auth_config.find(item => item.type === 'OIDC') == null) {
      createAccountSignIn(payload).then((response: ConfigResponse) => {
        console.log('createAccountSignIn response::', response)
        setFederationId(response.id)
        setCallBackUrl(response.service_provider_config.callback_url)
        setLoginEndPoint(response.service_provider_config.login_endpoint)
        setLogoutEndpoint(response.service_provider_config.logout_endpoint)
        setStatus(response.status)
      })
    }

    else {
      const federationProvider = data.auth_config ? data.auth_config.find(item => item.type === 'OIDC') : undefined
      const federationId = federationProvider ? federationProvider.id : ''
      setFederationId(federationId)
      getAccountSignIn(federationId).then((res: any) => {
        setCallBackUrl(res.service_provider_config.callback_url)
        setLoginEndPoint(res.service_provider_config.login_endpoint)
        setLogoutEndpoint(res.service_provider_config.logout_endpoint)
        setStatus(res.status)
        setProviderUrl(res.identity_provider_config.provider_url)
        setClientId(res.identity_provider_config.client_id)
        setClientSecret(res.identity_provider_config.client_secret)
        setTokenAudience(res.identity_provider_config.token_audience)
      })
    }
  }

  useEffect(() => {
    if(!account){
      fetchTenantById(props.match.params.id).then((res: any) => {
        setAccount(res)
        setShowInfo(disableSigninMethod(res, 'ssoOidc'))
      })
    }

    else {
      setOidcInfo(account)
    }
  }, [account])

  const onTokenAudienceChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTokenAudience(event.target.value)
  }

  const onClientIdChange = (event: ChangeEvent<HTMLInputElement>) => {
    setClientId(event.target.value)
  }

  const onClientSecretChange = (event: ChangeEvent<HTMLInputElement>) => {
    setClientSecret(event.target.value)
  }

  const onProviderUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
    setProviderUrl(event.target.value)
  }

  const onEnableDisable = () => {
    if (status === 'ENABLED') {
      setStatus('DISABLED')
    } else {
      setStatus('ENABLED')
    }
  }

  const onClick = () : void => {
    account && account.system_account ? history.push(RouterConfig.platformSettings.settings)  : history.push(RouterConfig.accounts.details(account?.id))
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const payload =
      {
        auth_type:'OIDC',
        account_id: account && account.id,
        identity_provider_config: {
          provider_url : providerUrl,
          token_audience: tokenAudience,
          client_id: clientId,
          client_secret: clientSecret
        },
        status:status
      }
    updateIdpMetadata(payload, federationId).then(() => {
      account && account.system_account ? history.push(RouterConfig.platformSettings.settings)  : history.push(RouterConfig.accounts.details(account?.id))
    })
  }

  const editClientSecret = (e) => {
    e.preventDefault()
    setPreviousClientSecret(clientSecret)
    setEditingClientSecret(true)
    setClientSecret('')
  }

  const cancelEditClientSecret = (e) => {
    e.preventDefault()
    setEditingClientSecret(false)
    setClientSecret(previousClientSecret)
  }

  const updateEditClientSecret = (e) => {
    e.preventDefault()
    setEditingClientSecret(false)
    clientSecret === '' ? setClientSecret(previousClientSecret) : setClientSecret(clientSecret)
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>
          <Translate id={'openIdConnectIntegration.title'}/>
        </h1>
        <p>
          <Translate id='openIdConnectIntegration.desc'/>
        </p>

        <div className={styles.display + ' ' + styles.warningText}>
          <IncontextBanner
            bannerType="note"
            description={<Translate id='openIdConnectIntegration.note' />}
          />
        </div>
      </header>

      <div className={styles.display}>
        <div className={styles.options}>
          <div className={styles.option}>
            <input id='openId-connect-enabled' type='radio' name='enabled' value='enabled' checked={status === 'ENABLED'} onChange={onEnableDisable}/>
            <label htmlFor='openId-connect-enabled'>
              <Translate id={'openIdConnectIntegration.enable'}/>
            </label>
          </div>
          <div className={styles.option}>
            <input id='openId-connect-disabled' type='radio' name='enabled' value='disabled' disabled={showInfo} checked={status !== 'ENABLED'} onChange={onEnableDisable}/>
            <label htmlFor='openId-connect-disabled'>
              <Translate id={'openIdConnectIntegration.disable'}/>
            </label>
          </div>
        </div>

        <div className={styles.line}/>

        <div className={status !== 'ENABLED' ? styles.hide : styles.display}>
          <div className={styles.section}>
            <div className={cn(styles.caption, editingClientSecret && styles.disabledArea)}>
              <span className={styles.step}>1</span>
              <h3><Translate id='openIdConnectIntegration.step1'/></h3>
            </div>

            <div className={cn(styles.sectionBody, editingClientSecret && styles.disabledArea)}>
              <div className={styles.caption}>
                <span className={styles.details}><Translate id='openIdConnectIntegration.url1'/></span>
                <span className={styles.details}><CopyToClipboard text={callBackUrl}>
                  <FontAwesomeIcon size='lg' icon={faCopy} />
                  </CopyToClipboard>
                  </span><br/>
                <span className='details-label'>{callBackUrl}</span>
              </div>
              <div className={styles.caption}>
                <span className={styles.details}><Translate id='openIdConnectIntegration.url2'/></span>
                <span className={styles.details}><CopyToClipboard text={loginEndpoint}>
                  <FontAwesomeIcon size='lg' icon={faCopy} />
                  </CopyToClipboard>
                  </span><br/>
                <span className='details-label'>{loginEndpoint}</span>
              </div>
              <div className={styles.caption}>
                <span className={styles.details}><Translate id='openIdConnectIntegration.url3'/></span>
                <span className={styles.details}><CopyToClipboard text={logoutEndpoint}>
                  <FontAwesomeIcon size='lg' icon={faCopy} />
                  </CopyToClipboard>
                  </span><br/>
                <span className='details-label'>{logoutEndpoint}</span>
              </div>
            </div>

            <div className={styles.caption}>
              <span className={styles.step}>2</span>
              <h3><Translate id='openIdConnectIntegration.step2'/></h3>
            </div>

            <div className={styles.sectionBody}>
              <Form center={false} onSubmit={(e) => {onSubmit(e).then()}}>
                <FormGroup>
                  <Input
                    label={translate('openIdConnectIntegration.form.providerUrl')}
                    size={FormFieldSize.XL}
                    minLength={1}
                    maxLength={256}
                    value={providerUrl}
                    onChange={onProviderUrlChange}
                    id='providerUrl'
                  />
                </FormGroup>

                <div className={styles.clientSecretGroup}>
                  <div>
                    <Input
                      label={
                        <>
                          <Translate id='openIdConnectIntegration.form.clientSecret'/>&nbsp;
                          {authConfigStatus && authConfigStatus !== 'INITIAL' && (
                            <button
                              className={cn(styles.editClientSecret, editingClientSecret && styles.disabledEditClientSecret)}
                              onClick={editClientSecret}
                            >
                              <FontAwesomeIcon size='lg' icon={faPencil}/>
                            </button>
                          )}
                        </>
                      }
                      size={FormFieldSize.XL}
                      minLength={1}
                      maxLength={256}
                      value={clientSecret}
                      onChange={onClientSecretChange}
                      id='clientSecret'
                      disabled={authConfigStatus && authConfigStatus !== 'INITIAL' && !editingClientSecret}
                    />
                  </div>
                  {authConfigStatus && authConfigStatus !== 'INITIAL' && editingClientSecret && (
                    <div>
                      <Button buttonType={ButtonType.SECONDARY} onClick={cancelEditClientSecret}><Translate id='common.form.buttons.cancelBtn'/></Button>
                      <Button onClick={updateEditClientSecret}><Translate id='common.updateClientSecret' /></Button>
                    </div>
                  )}
                </div>

                <FormGroup>
                  <Input
                    label={translate('openIdConnectIntegration.form.clientId')}
                    size={FormFieldSize.XL}
                    minLength={1}
                    maxLength={256}
                    value={clientId}
                    onChange={onClientIdChange}
                    id='clientId'
                  />
                </FormGroup>
                <FormGroup>
                  <Input
                    label={translate('openIdConnectIntegration.form.tokenAudience')}
                    size={FormFieldSize.XL}
                    minLength={1}
                    maxLength={256}
                    value={tokenAudience}
                    onChange={onTokenAudienceChange}
                    id='tokenAudience'
                  />
                </FormGroup>
              </Form>
            </div>
          </div>
        </div>
      </div>

      <div className={cn(styles.section, editingClientSecret && styles.disabledArea)}>
        <div className={styles.sectionBody}>
          <Form>
            <FormGroup colWidth={FormFieldSize.M}>
              <Button buttonType={ButtonType.SECONDARY} onClick={onClick}>
                <Translate id='common.form.buttons.cancelBtn' />
              </Button>
              <SubmitButton buttonType={ButtonType.PRIMARY} onClick={onSubmit}>
                <Translate id='common.form.buttons.updateOidc' />
              </SubmitButton>
            </FormGroup>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default connect(
  (state: IAppState, ownProps: OpenIdConnectIntegrationOwnProps) : OpenIdConnectIntegrationStateProps => ({
    tenant: state.tenants.items.find(item => item.id === ownProps.match.params.id)
  }),
  { createAccountSignIn, fetchTenantById, getAccountSignIn, updateIdpMetadata }
)(OpenIdConnectIntegration)